import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import {
  WorkOrderAssignmentPayoutRecord,
  workOrderAssignmentPayoutRecordQuery,
} from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutRecordQuery";
import { updateWorkOrderAssignmentPayout } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrderPayoutMutations";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { DateInput } from "@/lib/Components/Form/Inputs/DateInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { PayoutStatus } from "@/gql/graphql";
import dayjs from "dayjs";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import { LandmarkIcon } from "lucide-react";
import { Suspense } from "react";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { z } from "zod";
import { useTranslations } from "use-intl";
import { Badge } from "@/components/catalyst/badge";
import { workOrderStatusMap } from "@/app/ProDrivers/WorkOrders/utils/workOrderStatusMap";

const validation = z.object({
  paid_at: z.string().min(1, { error: "Paid at is required" }),
  transaction_reference: z
    .string()
    .min(1, { error: "Transaction reference is required" }),
});

type FormValues = {
  paid_at: string;
  transaction_reference: string;
};

export function ProcessDriverPayoutDialog({ payoutId }: { payoutId: string }) {
  return (
    <BaseDialog title="Process Driver Payout" size="2xl">
      <Suspense fallback={<div className="p-4 text-center">Loading...</div>}>
        <ProcessDriverPayoutContent payoutId={payoutId} />
      </Suspense>
    </BaseDialog>
  );
}

function ProcessDriverPayoutContent({ payoutId }: { payoutId: string }) {
  const { data } = useSuspenseGqlQuery(workOrderAssignmentPayoutRecordQuery, {
    id: payoutId,
  });
  const { mutateAsync: updatePayout } = useGqlMutation(
    updateWorkOrderAssignmentPayout,
  );
  const { close } = useDialogState();

  const payout = data.workOrderAssignmentPayout;

  if (!payout) {
    return <div className="p-4">Payout not found</div>;
  }

  const proDriver = payout.workOrderAssignment.proDriver;

  return (
    <div className="space-y-6">
      <PayoutCard payout={payout} />

      <DriverDetailsCard payout={payout} />

      {proDriver ? (
        <BankDetailsCard
          bankName={proDriver.bank_name}
          accountName={proDriver.bank_account_name}
          accountNumber={proDriver.bank_account_number}
          identifierCode={proDriver.bank_identifier_code}
        />
      ) : null}

      <div>
        <GenericForm<FormValues>
          validate={toFormikValidate(validation)}
          initialValues={{
            paid_at: dayjs().format("YYYY-MM-DD"),
            transaction_reference: "",
          }}
          onSubmit={async (values) => {
            await updatePayout({
              id: payout.id,
              input: {
                status: PayoutStatus.Paid,
                paid_at: values.paid_at,
                transaction_reference: values.transaction_reference,
              },
            });

            close();
          }}
        >
          <DateInput
            name="paid_at"
            label="Paid At"
            max={dayjs().format("YYYY-MM-DD")}
            className="col-span-full lg:col-span-6"
          />
          <TextInput
            name="transaction_reference"
            label="Transaction Reference"
            className="col-span-full lg:col-span-6"
          />
          <DialogSubmitButton text="Confirm Payout" />
        </GenericForm>
      </div>
    </div>
  );
}

function PayoutCard({ payout }: { payout: WorkOrderAssignmentPayoutRecord }) {
  const driverName = payout.workOrderAssignment.proDriver?.name ?? "—";
  const reference = payout.workOrderAssignment.workOrder.reference;
  const t = useTranslations("common.tax_type");

  return (
    <div className="overflow-hidden rounded-lg border border-zinc-200">
      <div className="border-b border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600">
        Payout to <strong>{driverName}</strong> for <strong>{reference}</strong>
      </div>

      {payout.lineItems.length > 0 ? (
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs uppercase text-zinc-600">
            <tr>
              <th className="px-3 py-2 text-left">Expense</th>
              <th className="px-3 py-2 text-left">Tax</th>
              <th className="px-3 py-2 text-right">Tax Amt</th>
              <th className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {payout.lineItems.map((line) => (
              <tr key={line.id}>
                <td className="px-3 py-2">{line.description ?? "--"}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">
                  {t(line.tax_type)}
                </td>
                <td className="px-3 py-2 text-right">
                  {formatCurrency(line.amount_tax, payout.currency)}
                </td>
                <td className="px-3 py-2 text-right font-medium">
                  {formatCurrency(line.amount_inc_tax, payout.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      <div className="space-y-1 border-t border-zinc-200 bg-zinc-50 px-4 py-3 text-sm">
        <div className="flex justify-between">
          <span className="text-zinc-600">Tax</span>
          <span className="font-medium">
            {formatCurrency(payout.amount_tax, payout.currency)}
          </span>
        </div>
        <div className="flex justify-between border-t border-zinc-200 pt-1 font-semibold">
          <span>Total</span>
          <span>{formatCurrency(payout.amount, payout.currency)}</span>
        </div>
      </div>
    </div>
  );
}

function DriverDetailsCard({
  payout,
}: {
  payout: WorkOrderAssignmentPayoutRecord;
}) {
  const t = useTranslations("workOrder");
  const proDriver = payout.workOrderAssignment.proDriver;
  const workOrder = payout.workOrderAssignment.workOrder;

  const dates =
    workOrder.depart_after && workOrder.deliver_before
      ? `${dayjs(workOrder.depart_after).format("DD MMM YYYY")} - ${dayjs(
          workOrder.deliver_before,
        ).format("DD MMM YYYY")}`
      : null;

  return (
    <div>
      <h3 className="mb-3 text-sm font-medium text-zinc-700">
        Driver & Work Order
      </h3>
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-zinc-500">Work Order</span>
            <CopyToClipboard text={workOrder.reference}>
              <div className="font-medium">{workOrder.reference || "--"}</div>
            </CopyToClipboard>
          </div>
          <div>
            <span className="text-zinc-500">Dates</span>
            <CopyToClipboard text={dates}>
              <div className="font-medium">{dates || "--"}</div>
            </CopyToClipboard>
          </div>
          <div>
            <span className="text-zinc-500">Status</span>
            <div className="mt-0.5">
              <Badge color={workOrderStatusMap[workOrder.status]}>
                {t(`status.${workOrder.status}`)}
              </Badge>
            </div>
          </div>
          <div>
            <span className="text-zinc-500">Driver Name</span>
            <CopyToClipboard text={proDriver?.name ?? null}>
              <div className="font-medium">{proDriver?.name || "--"}</div>
            </CopyToClipboard>
          </div>
          <div>
            <span className="text-zinc-500">Email</span>
            <CopyToClipboard text={proDriver?.email ?? null}>
              <div className="font-medium">{proDriver?.email || "--"}</div>
            </CopyToClipboard>
          </div>
          <div>
            <span className="text-zinc-500">Phone</span>
            <CopyToClipboard text={proDriver?.phone ?? null}>
              <div className="font-medium">{proDriver?.phone || "--"}</div>
            </CopyToClipboard>
          </div>
        </div>
      </div>
    </div>
  );
}

function BankDetailsCard({
  bankName,
  accountName,
  accountNumber,
  identifierCode,
}: {
  bankName?: string | null;
  accountName?: string | null;
  accountNumber?: string | null;
  identifierCode?: string | null;
}) {
  return (
    <div>
      <h3 className="mb-3 text-sm font-medium text-zinc-700">Bank Details</h3>
      <div className="rounded-lg border border-zinc-200 bg-white p-4">
        <div className="flex items-start gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-50">
            <LandmarkIcon className="size-5 text-blue-600" />
          </div>
          <div className="grid flex-1 grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-zinc-500">Bank Name</span>
              <CopyToClipboard text={bankName ?? null}>
                <div className="font-medium">{bankName || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Account Name</span>
              <CopyToClipboard text={accountName ?? null}>
                <div className="font-medium">{accountName || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">Account Number</span>
              <CopyToClipboard text={accountNumber ?? null}>
                <div className="font-medium">{accountNumber || "--"}</div>
              </CopyToClipboard>
            </div>
            <div>
              <span className="text-zinc-500">BSB / Sort Code</span>
              <CopyToClipboard text={identifierCode ?? null}>
                <div className="font-medium">{identifierCode || "--"}</div>
              </CopyToClipboard>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
